/**
 * PDFEditor Component
 *
 * Main PDF editing interface combining:
 * - PDF viewer with redaction support
 * - AI-powered entity detection and suggestions
 * - Zoom controls
 * - Page navigation
 * - Redaction toolbar
 * - Undo/redo functionality
 * - Side-by-side view option
 */

import { useState, useCallback, useEffect } from 'react';
import {
  RotateCcw,
  RotateCw,
  Columns,
  Loader2,
} from 'lucide-react';
import { PDFViewer } from './PDFViewer';
import { ZoomControls } from './ZoomControls';
import { PageNavigation } from './PageNavigation';
import { RedactionToolbar } from './RedactionToolbar';
import { SplitView } from './SplitView';
import { useCommandHistory } from '../../hooks/useCommandHistory';
import type { RedactionTool, Redaction, EntitySuggestion } from '../../types';

interface PDFEditorProps {
  pdfData: ArrayBuffer | Uint8Array | string; // string for Blob URLs
  documentId?: string;
  initialRedactions?: Redaction[];
  /** Suggestions passed from parent (already loaded at case level) */
  suggestions?: EntitySuggestion[];
  /** Selected suggestion index from parent (for navigation from ResultsPanel) */
  selectedSuggestionIndex?: number | null;
  /** Callback when user selects a suggestion */
  onSelectSuggestion?: (index: number | null) => void;
}

export function PDFEditor({
  pdfData,
  initialRedactions = [],
  suggestions: propSuggestions = [],
  selectedSuggestionIndex,
  onSelectSuggestion,
}: PDFEditorProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [activeTool, setActiveTool] = useState<RedactionTool>('blackout');
  const [selectedRedaction, setSelectedRedaction] = useState<string | null>(null);
  const [isSplitView, setIsSplitView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // AI Detection state - controlled by parent
  const [localSelectedSuggestion, setLocalSelectedSuggestion] = useState<number | null>(null);

  // Use parent's selected suggestion if provided, otherwise use local state
  const selectedSuggestion = selectedSuggestionIndex ?? localSelectedSuggestion;
  const setSelectedSuggestion = onSelectSuggestion ?? setLocalSelectedSuggestion;

  // Use suggestions directly from props (controlled component)
  const suggestions = propSuggestions;

  // Navigate to suggestion when selectedSuggestionIndex changes from parent (ResultsPanel)
  useEffect(() => {
    if (selectedSuggestionIndex != null) {
      const suggestion = suggestions.find(s => s.index === selectedSuggestionIndex);
      if (suggestion) {
        // Pages in suggestions are 0-indexed, UI is 1-indexed
        setCurrentPage(suggestion.page + 1);
      }
    }
  }, [selectedSuggestionIndex, suggestions]);

  // Use command history for undo/redo
  const {
    redactions,
    canUndo,
    canRedo,
    addRedaction,
    removeRedaction,
    undo,
    redo,
  } = useCommandHistory(initialRedactions);

  // Handle document load
  const handleDocumentLoad = useCallback((numPages: number) => {
    setTotalPages(numPages);
    setIsLoading(false);
  }, []);

  // Handle redaction add
  const handleRedactionAdd = useCallback((redaction: Omit<Redaction, 'id' | 'createdAt'>) => {
    addRedaction(redaction);
  }, [addRedaction]);

  // Handle delete selected
  const handleDeleteSelected = useCallback(() => {
    if (selectedRedaction) {
      removeRedaction(selectedRedaction);
      setSelectedRedaction(null);
    }
  }, [selectedRedaction, removeRedaction]);

  // Handle error from PDF viewer
  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    setIsLoading(false);
  }, []);

  // Handle suggestion click from PDF viewer
  const handleSuggestionClick = useCallback((index: number) => {
    setSelectedSuggestion(index === selectedSuggestion ? null : index);
  }, [selectedSuggestion]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Escape to deselect
      if (e.key === 'Escape') {
        setSelectedRedaction(null);
        setActiveTool('blackout');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Render PDF viewer (used in both single and split view)
  const renderViewer = (showRedactions: boolean, showSuggestions: boolean = true) => (
    <div className="flex items-center justify-center p-8">
      <PDFViewer
        pdfData={pdfData}
        currentPage={currentPage}
        zoom={zoom}
        activeTool={showRedactions ? activeTool : 'select'}
        redactions={showRedactions ? redactions : []}
        suggestions={showSuggestions ? suggestions : []}
        selectedSuggestion={selectedSuggestion}
        onPageChange={setCurrentPage}
        onDocumentLoad={handleDocumentLoad}
        onRedactionAdd={handleRedactionAdd}
        onRedactionSelect={setSelectedRedaction}
        onSuggestionClick={handleSuggestionClick}
        selectedRedaction={selectedRedaction}
        onError={handleError}
      />
    </div>
  );

  return (
    <div className="flex flex-col flex-1 bg-background h-full overflow-hidden">
      {/* Toolbar */}
      <div className="bg-card border-b border-border px-6 py-3">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left: Undo/Redo, Zoom, Page Navigation */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Undo/Redo */}
            <div className="flex items-center gap-1">
              <button
                onClick={undo}
                disabled={!canUndo}
                className="p-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Undo (Ctrl+Z)"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                onClick={redo}
                disabled={!canRedo}
                className="p-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Redo (Ctrl+Y)"
              >
                <RotateCw className="w-4 h-4" />
              </button>
            </div>

            <div className="w-px h-6 bg-border" />

            {/* Zoom Controls */}
            <ZoomControls
              zoom={zoom}
              onZoomChange={setZoom}
            />

            <div className="w-px h-6 bg-border" />

            {/* Page Navigation */}
            {totalPages > 0 && (
              <PageNavigation
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>

          {/* Right: Redaction Tools and Actions */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Redaction Toolbar */}
            <RedactionToolbar
              activeTool={activeTool}
              onToolChange={setActiveTool}
              onDeleteSelected={handleDeleteSelected}
              hasSelection={selectedRedaction !== null}
            />

            <div className="w-px h-6 bg-border" />

            {/* View Toggle */}
            <button
              onClick={() => setIsSplitView(!isSplitView)}
              className={`flex items-center gap-2 px-3 py-2 rounded-[var(--radius-button)] transition-colors ${
                isSplitView ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
              }`}
              title="Toggle split view"
            >
              <Columns className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">Split View</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-6 py-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Document View */}
      <div className="flex-1 overflow-hidden flex min-h-0">
        {/* Main content area */}
        <div className="flex-1 overflow-hidden relative min-h-0">
          {/* Loading overlay - shown on top of viewer while loading */}
          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                <p className="text-muted-foreground">Loading document...</p>
              </div>
            </div>
          )}
          {/* Always render the viewer so it can load the PDF and call onDocumentLoad */}
          {isSplitView ? (
            <SplitView
              leftPanel={renderViewer(false, false)}
              rightPanel={renderViewer(true, true)}
              leftTitle="Original"
              rightTitle="With Redactions"
              onClose={() => setIsSplitView(false)}
              syncScroll={true}
            />
          ) : (
            <div className="h-full overflow-auto bg-muted">
              {renderViewer(true, true)}
            </div>
          )}
        </div>

      </div>

      {/* Status Bar */}
      <div className="bg-card border-t border-border px-6 py-2 text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <span>•</span>
            <span>Zoom: {zoom}%</span>
            <span>•</span>
            <span>{redactions.length} redaction{redactions.length !== 1 ? 's' : ''}</span>
            {suggestions.length > 0 && (
              <>
                <span>•</span>
                <span className="text-primary">
                  {suggestions.filter(s => s.status === 'pending').length} AI suggestions pending
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            {canUndo && <span>Press Ctrl+Z to undo</span>}
            <span>Tool: {activeTool.charAt(0).toUpperCase() + activeTool.slice(1)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
